import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    HostBinding,
    Input,
    ViewEncapsulation
} from '@angular/core';
import { Icons } from '../icon/constants/icons.constants';
import { IconComponent } from '../icon/icon.component';
import { Button } from './models/button.type';

const SHARED_HOST = {
    '[attr.disabled]': '_getDisabledAttribute()',
    '[attr.aria-disabled]': '_getAriaDisabled()'
};

const BUILD_HOST = (
    type: Button.Type,
    disabled: boolean,
    active: boolean | undefined,
    color: Button.Color | null | undefined,
    size: Button.Size | null | undefined
): string => {
    const TYPE: string = type === 'default' ? '' : `-${type}`;
    let host_classes: string = `nw${TYPE}-button `;

    if (disabled) host_classes = host_classes.concat(`nw${TYPE}-button--disabled `);
    if (active) host_classes = host_classes.concat(`nw${TYPE}-button--active `);
    if (!color) host_classes = host_classes.concat(`nw${TYPE}-button--primary `);
    if (color) host_classes = host_classes.concat(`nw${TYPE}-button--${color} `);
    if (!size) host_classes = host_classes.concat(`nw${TYPE}-button--m `);
    if (size) host_classes = host_classes.concat(`nw${TYPE}-button--${size} `);

    return host_classes;
};

@Component({
    template: '',
    standalone: false
})
class ButtonBase {
    @Input({ transform: booleanAttribute })
    get disabled(): boolean {
        return this._disabled;
    }
    set disabled(value: any) {
        this._disabled = value;
    }
    private _disabled: boolean = false;

    @Input({ transform: booleanAttribute, alias: 'aria-disabled' })
    ariaDisabled: boolean | undefined;

    @Input({ transform: booleanAttribute })
    active: boolean | undefined;

    @Input() color?: Button.Color | null;
    @Input() size?: Button.Size | null;
    @Input() labelClass: string = '';

    protected _getDisabledAttribute() {
        return !this.disabled ? null : true;
    }

    protected _getAriaDisabled() {
        if (this.ariaDisabled != null) {
            return this.ariaDisabled;
        }

        return this.disabled ? true : null;
    }
}

@Component({
    selector: `
      button[nw-button],
      a[nw-button]
    `,
    templateUrl: 'button.component.html',
    styleUrl: 'button.component.scss',
    host: {
        ...SHARED_HOST
    },
    exportAs: 'nwButton',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ButtonComponent extends ButtonBase {
    @HostBinding('class') get classes(): string {
        return BUILD_HOST('default', this.disabled, this.active, this.color, this.size);
    }
}

@Component({
    selector: `
      button[nw-outline-button],
       a[nw-outline-button]
    `,
    templateUrl: 'button.component.html',
    styleUrl: 'button.component.scss',
    host: {
        ...SHARED_HOST
    },
    exportAs: 'nwButton',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OutlineButtonComponent extends ButtonBase {
    @HostBinding('class') get classes(): string {
        return BUILD_HOST('outline', this.disabled, this.active, this.color, this.size);
    }
}

@Component({
    selector: `
      button[nw-ghost-button],
      a[nw-ghost-button]
    `,
    templateUrl: 'button.component.html',
    styleUrl: 'button.component.scss',
    host: {
        ...SHARED_HOST
    },
    exportAs: 'nwButton',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class GhostButtonComponent extends ButtonBase {
    @HostBinding('class') get classes(): string {
        return BUILD_HOST('ghost', this.disabled, this.active, this.color, this.size);
    }
}

@Component({
    selector: `
      button[nw-icon-button],
      a[nw-icon-button]
    `,
    templateUrl: 'button.component.html',
    styleUrl: 'button.component.scss',
    host: {
        ...SHARED_HOST
    },
    exportAs: 'nwButton',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class IconButtonComponent extends ButtonBase {
    @HostBinding('class') get classes(): string {
        return BUILD_HOST('icon', this.disabled, this.active, this.color, this.size);
    }
}

@Component({
    selector: `
      button[nw-animated-icon-button],
      a[nw-animated-icon-button]
    `,
    template: `<span class="nw-button__label {{ labelClass }}"
        ><ng-content></ng-content
        ><nw-icon class="nw-button__label__icon" [icon]="icon"></nw-icon
    ></span>`,
    styleUrl: 'button.component.scss',
    host: {
        ...SHARED_HOST
    },
    exportAs: 'nwButton',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [IconComponent]
})
export class AnimatedIconButtonComponent extends ButtonBase {
    @Input() icon!: Icons.Type;
    @Input()
    @HostBinding('class.nw-animated-icon-button--animate-from-left')
    invert: boolean = false;

    @HostBinding('class') get classes(): string {
        return BUILD_HOST(
            'animated-icon',
            this.disabled,
            this.active,
            this.color,
            this.size
        );
    }
}

@Component({
    selector: `
      button[nw-animated-icon-outline-button],
      a[nw-animated-icon-outline-button]
    `,
    template: `<span class="nw-button__label {{ labelClass }}"
        ><ng-content></ng-content
        ><nw-icon class="nw-button__label__icon" [icon]="icon"></nw-icon
    ></span>`,
    styleUrl: 'button.component.scss',
    host: {
        ...SHARED_HOST
    },
    exportAs: 'nwButton',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [IconComponent]
})
export class AnimatedIconOutlineButtonComponent extends ButtonBase {
    @Input() icon!: Icons.Type;
    @HostBinding('class.nw-animated-icon-outline-button--animate-from-left')
    invert: boolean = false;

    @HostBinding('class') get classes(): string {
        return BUILD_HOST(
            'animated-icon-outline',
            this.disabled,
            this.active,
            this.color,
            this.size
        );
    }
}
